import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// "Chi siamo" top: breadcrumb + title on the page background, then a carousel of the four photos.
// Every photo is shown whole at one shared height (native files, never cropped), side by side on a scroll-snap track.
const PHOTOS = [
  { src: "/founders/about-carousel-1.jpg", alt: "Alessia e Alessandra — FArLight, in movimento", w: 1335, h: 1600 },
  { src: "/founders/about-carousel-2.jpg", alt: "Alessia e Alessandra — FArLight, sedute schiena contro schiena", w: 925, h: 1152 },
  { src: "/founders/about-carousel-3.jpg", alt: "Il nostro studio, in movimento", w: 1234, h: 1600 },
  { src: "/founders/about-carousel-4.jpg", alt: "Alessia e Alessandra — FArLight", w: 1172, h: 600 },
];

export const AboutCarousel = ({ label }) => {
  const track = useRef(null);
  const [paused, setPaused] = useState(false);

  const step = (dir) => {
    const el = track.current;
    if (!el) return;
    const slides = Array.from(el.children);
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    if (dir > 0 && atEnd) return el.scrollTo({ left: 0, behavior: "smooth" });
    if (dir < 0 && el.scrollLeft <= 4) return el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    // a slide's snap position is its left edge minus the track's left padding
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const pos = (s) => s.offsetLeft - pad;
    const target = dir > 0 ? slides.find((s) => pos(s) > el.scrollLeft + 8) : [...slides].reverse().find((s) => pos(s) < el.scrollLeft - 8);
    if (target) el.scrollTo({ left: pos(target), behavior: "smooth" });
  };

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => step(1), 5000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section data-testid="about-banner" className="pt-24 pb-12 lg:pb-16 bg-cream" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inksoft">
          <Link to="/" className="hover:text-ink">Home</Link> · {label}
        </p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">{label}</h1>
      </div>
      <div className="relative mt-8">
        <div ref={track} data-testid="about-carousel" className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory pr-6 lg:pr-12 pl-[max(1.5rem,calc((100%-80rem)/2+1.5rem))] lg:pl-[max(3rem,calc((100%-80rem)/2+3rem))] scroll-pl-[max(1.5rem,calc((100%-80rem)/2+1.5rem))] lg:scroll-pl-[max(3rem,calc((100%-80rem)/2+3rem))] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {PHOTOS.map((p, i) => (
            <img key={p.src} data-testid={`about-banner-photo-${i}`} src={p.src} alt={p.alt} width={p.w} height={p.h} loading={i === 0 ? "eager" : "lazy"} draggable={false}
              className="snap-start shrink-0 w-auto max-w-none h-[52vh] min-h-[320px] max-h-[600px] rounded-sm" />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-5 flex justify-end gap-2">
          <button type="button" data-testid="about-carousel-prev" aria-label="Foto precedente" onClick={() => step(-1)} className="h-10 w-10 grid place-items-center rounded-full border border-ink/25 text-ink hover:bg-ink hover:text-cream transition-colors">
            <ChevronLeft size={18} />
          </button>
          <button type="button" data-testid="about-carousel-next" aria-label="Foto successiva" onClick={() => step(1)} className="h-10 w-10 grid place-items-center rounded-full border border-ink/25 text-ink hover:bg-ink hover:text-cream transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
